import React from "react";
import { Box, useMediaQuery } from "@mui/material";
import { dt } from "../../../shared/themes/redesignTokens";
import { CatalogCategory } from "../models/models";
import useTranslation from "../../../shared/hooks/useTranslation";
import { getUiLocale } from "../../../shared/utils/relativeTime";

interface CatalogCategoryTabsProps {
  categories: CatalogCategory[];
  selectedCode: string | undefined;
  totalCount: number;
  onSelect: (categoryCode: string | undefined) => void;
}

const categoryColorMap: Record<string, string> = {
  "machining-centers": dt.blue,
  "turning-machines": "#8E44AD",
  "grinding-machines": "#E67E22",
  "edm": "#27AE60",
  "laser-machines": "#E74C3C",
  "packaging-lines": "#795548",
};

/** Maps category code to a short mobile-friendly label key */
const categoryShortLabelMap: Record<string, string> = {
  "machining-centers": "machiningCentersShort",
  "turning-machines": "turningMachinesShort",
  "grinding-machines": "grindingMachinesShort",
  "edm": "edmMachines",
  "laser-machines": "laserMachinesShort",
  "packaging-lines": "packagingLinesShort",
};

/** Maps category code to a full label key */
const categoryLabelMap: Record<string, string> = {
  "machining-centers": "machiningCenters",
  "turning-machines": "turningMachines",
  "grinding-machines": "grindingMachines",
  "edm": "edmMachines",
  "laser-machines": "laserMachines",
  "packaging-lines": "packagingLines",
};

const CatalogCategoryTabs: React.FC<CatalogCategoryTabsProps> = ({
  categories,
  selectedCode,
  totalCount,
  onSelect,
}) => {
  const isPhone = useMediaQuery("(max-width:768px)");
  const { translate } = useTranslation("myMachinery");

  const getColor = (code?: string): string => {
    if (!code) return dt.blue;
    return categoryColorMap[code] || dt.blue;
  };

  const darkenColor = (hex: string): string => {
    // Simple darken: reduce each component by ~15%
    const r = Math.max(0, parseInt(hex.slice(1, 3), 16) - 30);
    const g = Math.max(0, parseInt(hex.slice(3, 5), 16) - 30);
    const b = Math.max(0, parseInt(hex.slice(5, 7), 16) - 30);
    return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
  };

  const isActive = (code: string | undefined): boolean => {
    return selectedCode === code;
  };

  const tabs: Array<{ code: string | undefined; label: string; count: number }> = [
    { code: undefined, label: translate("allCategories"), count: totalCount },
    ...categories.map((cat) => ({
      code: cat.code,
      label: isPhone
        ? translate(categoryShortLabelMap[cat.code] || cat.name)
        : translate(categoryLabelMap[cat.code] || cat.name),
      count: cat.modelCount,
    })),
  ];

  return (
    <Box
      sx={{
        display: "flex",
        gap: isPhone ? "8px" : "10px",
        overflowX: "auto",
        WebkitOverflowScrolling: "touch",
        pb: "4px",
        "&::-webkit-scrollbar": { display: "none" },
        scrollbarWidth: "none",
        animation: "headerReveal 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.1s both",
      }}
    >
      {tabs.map((tab) => {
        const active = isActive(tab.code);
        const color = getColor(tab.code);
        const dark = darkenColor(color);

        return (
          <Box
            key={tab.code || "all"}
            onClick={() => onSelect(tab.code)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              padding: isPhone ? "7px 14px" : "8px 18px",
              borderRadius: dt.radiusPill,
              cursor: "pointer",
              flexShrink: 0,
              whiteSpace: "nowrap",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              ...(active
                ? {
                    background: `linear-gradient(135deg, ${color} 0%, ${dark} 100%)`,
                    color: "#FFFFFF",
                    boxShadow: `0 2px 8px ${color}40`,
                  }
                : {
                    background: dt.cardBg,
                    border: `1px solid ${dt.cardBorder}`,
                    color: dt.textSecondary,
                    "&:hover": {
                      background: dt.inputBg,
                      borderColor: `${color}60`,
                    },
                  }),
            }}
          >
            <Box
              component="span"
              sx={{
                fontFamily: dt.font,
                fontSize: isPhone ? "12px" : "13px",
                fontWeight: active ? 600 : 400,
              }}
            >
              {tab.label}
            </Box>
            <Box
              component="span"
              sx={{
                fontFamily: dt.fontMono,
                fontSize: "11px",
                opacity: 0.8,
              }}
            >
              ({tab.count.toLocaleString(getUiLocale())})
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default CatalogCategoryTabs;
